import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const TARGET_FIELDS = [
  { key: 'DateTime', label: 'Date/Time', required: true, allowMultiple: true },
  { key: 'Ticker', label: 'Ticker/Symbol', required: true },
  { key: 'Type', label: 'Transaction Type', required: true },
  { key: 'Price', label: 'Price', required: false },
  { key: 'Quantity', label: 'Quantity', required: true },
  { key: 'Fees', label: 'Fees', required: false },
  { key: 'Currency', label: 'Currency', required: false },
  { key: 'CoingeckoId', label: 'Coingecko ID', required: false },
  { key: 'Notes', label: 'Notes', required: false },
  { key: 'Location', label: 'Location', required: false },
  { key: 'SourceLocation', label: 'Source Location', required: false },
  { key: 'DestinationLocation', label: 'Destination Location', required: false },
];

const EditMappingModal = ({ isOpen, onClose, onSave, loading, mappingPlan, originalRow }) => {
  // mappingPlan: { fields: [{ targetField, strategy: { kind, column, columns, separator } }] }
  // originalRow: { headers: [...], values: [...] }

  const [mappings, setMappings] = useState({});
  const [draggedColumn, setDraggedColumn] = useState(null);

  const headers = useMemo(() => originalRow?.headers || [], [originalRow?.headers]);
  const sampleValues = useMemo(() => originalRow?.values || [], [originalRow?.values]);

  // Build column info with sample values
  const sourceColumns = useMemo(() => {
    return headers.map((header, idx) => ({
      index: idx,
      header,
      sample: sampleValues[idx] || '',
    }));
  }, [headers, sampleValues]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Initialize mappings from existing plan
  useEffect(() => {
    if (!isOpen) return;
    
    const initial = {};
    if (mappingPlan?.fields) {
      mappingPlan.fields.forEach((field) => {
        if (!field?.targetField) return;
        const key = field.targetField;
        const columns = field.columns || [];
        
        if (field.strategyKind === 'column' && columns.length > 0) {
          const idx = headers.findIndex(
            (h) => h.toLowerCase() === columns[0].toLowerCase()
          );
          if (idx >= 0) {
            initial[key] = { columns: [idx], separator: ' ' };
          }
        } else if (field.strategyKind === 'combine' && columns.length > 0) {
          const indices = columns
            .map((col) => headers.findIndex((h) => h.toLowerCase() === col.toLowerCase()))
            .filter((i) => i >= 0);
          if (indices.length > 0) {
            initial[key] = { columns: indices, separator: ' ' };
          }
        }
      });
    }
    setMappings(initial);
  }, [isOpen, mappingPlan, headers]);

  // Get columns mapped to a target field
  const getMappedColumns = useCallback(
    (targetKey) => {
      const mapping = mappings[targetKey];
      if (!mapping?.columns?.length) return [];
      return mapping.columns.map((idx) => sourceColumns[idx]).filter(Boolean);
    },
    [mappings, sourceColumns]
  );

  // Get all used column indices
  const usedColumnIndices = useMemo(() => {
    const used = new Set();
    Object.values(mappings).forEach((m) => {
      m?.columns?.forEach((idx) => used.add(idx));
    });
    return used;
  }, [mappings]);

  // Unused columns for the source panel
  const unusedColumns = useMemo(
    () => sourceColumns.filter((col) => !usedColumnIndices.has(col.index)),
    [sourceColumns, usedColumnIndices]
  );

  const handleDragStart = (e, column) => {
    setDraggedColumn(column);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', column.index.toString());
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (!draggedColumn) return;

    const fieldDef = TARGET_FIELDS.find((f) => f.key === targetKey);
    const allowMultiple = fieldDef?.allowMultiple;

    setMappings((prev) => {
      const existing = prev[targetKey]?.columns || [];
      // If already contains this column, ignore
      if (existing.includes(draggedColumn.index)) return prev;
      
      const newColumns = allowMultiple
        ? [...existing, draggedColumn.index]
        : [draggedColumn.index];

      return {
        ...prev,
        [targetKey]: { columns: newColumns, separator: prev[targetKey]?.separator || ' ' },
      };
    });
    setDraggedColumn(null);
  };

  const handleRemoveColumn = (targetKey, columnIndex) => {
    setMappings((prev) => {
      const existing = prev[targetKey]?.columns || [];
      const newColumns = existing.filter((idx) => idx !== columnIndex);
      if (newColumns.length === 0) {
        const { [targetKey]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [targetKey]: { ...prev[targetKey], columns: newColumns },
      };
    });
  };

  const handleSeparatorChange = (targetKey, separator) => {
    setMappings((prev) => ({
      ...prev,
      [targetKey]: { ...prev[targetKey], separator },
    }));
  };

  const handleClearMapping = (targetKey) => {
    setMappings((prev) => {
      const { [targetKey]: _, ...rest } = prev;
      return rest;
    });
  };

  // Validation: check all required fields are mapped
  const missingRequired = useMemo(() => {
    return TARGET_FIELDS.filter((f) => f.required && !mappings[f.key]?.columns?.length).map(
      (f) => f.label
    );
  }, [mappings]);

  const canSave = missingRequired.length === 0;

  const handleSave = () => {
    if (!canSave) return;

    // Convert mappings to plan format
    const fields = Object.entries(mappings)
      .filter(([_, m]) => m?.columns?.length > 0)
      .map(([targetField, m]) => {
        if (m.columns.length === 1) {
          return {
            targetField,
            strategy: {
              kind: 'column',
              column: headers[m.columns[0]],
              columnIndex: m.columns[0],
            },
          };
        }
        return {
          targetField,
          strategy: {
            kind: 'combine',
            columns: m.columns.map((idx) => headers[idx]),
            columnIndices: m.columns,
            separator: m.separator || ' ',
          },
        };
      });

    onSave({ fields });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Edit Column Mapping
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Drag columns from the right to map them to target fields on the left.
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Left: Target Fields */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Target Fields
                </h3>
                <div className="space-y-3">
                  {TARGET_FIELDS.map((field) => {
                    const mapped = getMappedColumns(field.key);
                    return (
                      <div
                        key={field.key}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, field.key)}
                        className={`p-3 rounded-lg border-2 border-dashed transition-colors ${
                          mapped.length > 0
                            ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </span>
                          {mapped.length > 0 && (
                            <button
                              type="button"
                              onClick={() => handleClearMapping(field.key)}
                              className="text-xs text-gray-500 hover:text-red-500"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {mapped.length > 0 ? (
                          <div className="space-y-1">
                            {mapped.map((col) => (
                              <div
                                key={col.index}
                                className="flex items-center justify-between px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
                              >
                                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                  {col.header}
                                  <span className="text-gray-400 dark:text-gray-500 ml-2 text-xs">
                                    "{col.sample?.substring(0, 20)}{col.sample?.length > 20 ? '…' : ''}"
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveColumn(field.key, col.index)}
                                  className="ml-2 text-gray-400 hover:text-red-500"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                            {field.allowMultiple && mapped.length > 1 && (
                              <div className="flex items-center gap-2 mt-2 text-xs">
                                <span className="text-gray-500 dark:text-gray-400">Separator:</span>
                                <input
                                  type="text"
                                  value={mappings[field.key]?.separator || ' '}
                                  onChange={(e) => handleSeparatorChange(field.key, e.target.value)}
                                  className="w-12 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                  placeholder=" "
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                            Drop column here
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Source Columns */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Original Columns ({unusedColumns.length} unmapped)
                </h3>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {sourceColumns.map((col) => {
                    const isUsed = usedColumnIndices.has(col.index);
                    return (
                      <div
                        key={col.index}
                        draggable={!isUsed}
                        onDragStart={(e) => handleDragStart(e, col)}
                        onDragEnd={handleDragEnd}
                        className={`p-3 rounded-lg border transition-all ${
                          isUsed
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-grab hover:border-blue-400 hover:shadow-sm'
                        } ${draggedColumn?.index === col.index ? 'opacity-50' : ''}`}
                      >
                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                          {col.header}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {col.sample || <span className="italic">empty</span>}
                        </div>
                        {isUsed && (
                          <span className="text-xs text-green-600 dark:text-green-400 mt-1 inline-block">
                            ✓ Mapped
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {missingRequired.length > 0 && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                Missing required fields: {missingRequired.join(', ')}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || !canSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving…' : 'Save & Re-process'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditMappingModal;

